/**
 * Risk matrix calculator (free tool, /free/risk-matrix-calculator; SEO plan
 * Workstream E row 7, "Light" safety review).
 *
 * Pure logic, no React: likelihood and severity in, score and band out, for
 * the rating before controls and, optionally, after them. The 5 x 5 scale,
 * the three bands (1-4, 5-12, 13-25) and each band's action text are the
 * reviewed wording of /guides/risk-assessment-construction ("The likelihood x
 * severity matrix"), so the tool and the guide never disagree.
 *
 * The law (Management of Health and Safety at Work Regulations 1999, reg 3,
 * as HSE explains it) asks for a suitable and sufficient assessment and that
 * you decide how likely harm is and how serious it could be. It does not set
 * a scoring matrix: the page says so, and that scales vary between firms.
 *
 * Pinned for every combination (25 before x 26 after, including "no after
 * rating") in __tests__/lib/marketing/risk-matrix/matrix.test.ts.
 */
import { HSE_RISK_OVERVIEW_URL, HSE_RISK_STEPS_URL } from "./sources";

export type Level = 1 | 2 | 3 | 4 | 5;
export const LEVELS: readonly Level[] = [1, 2, 3, 4, 5];

/** Labels shown for each likelihood score (1 = very unlikely, 5 = very likely). */
export const LIKELIHOOD_LABELS: Record<Level, string> = {
  1: "Very unlikely",
  2: "Unlikely",
  3: "Possible",
  4: "Likely",
  5: "Very likely",
};

/** Labels shown for each severity score (1 = minor injury, 5 = fatality or permanent disability). */
export const SEVERITY_LABELS: Record<Level, string> = {
  1: "Minor injury",
  2: "Injury needing treatment",
  3: "Serious injury or illness",
  4: "Major injury or long-term illness",
  5: "Fatality or permanent disability",
};

export type BandId = "low" | "medium" | "high";

export interface Band {
  id: BandId;
  label: string;
  range: string;
  action: string;
}

export const BANDS: Record<BandId, Band> = {
  low: {
    id: "low",
    label: "Low risk",
    range: "1–4",
    action: "Acceptable with current controls. Monitor and maintain.",
  },
  medium: {
    id: "medium",
    label: "Medium risk",
    range: "5–12",
    action: "Additional controls are needed. Review whether you can reduce the likelihood or severity further.",
  },
  high: {
    id: "high",
    label: "High risk",
    range: "13–25",
    action:
      "Work should not proceed until additional controls bring the risk down. Consider whether the activity can be eliminated or done a completely different way.",
  },
};

export function score(likelihood: Level, severity: Level): number {
  return likelihood * severity;
}

export function bandFor(value: number): Band {
  if (value <= 4) return BANDS.low;
  if (value <= 12) return BANDS.medium;
  return BANDS.high;
}

export interface Rating {
  likelihood: Level;
  severity: Level;
}

export interface RatedRisk extends Rating {
  score: number;
  band: Band;
}

export interface Note {
  id: string;
  text: string;
  cite?: { label: string; url: string };
}

export const NOTES = {
  "severe-not-high": {
    id: "severe-not-high",
    text: "A hazard with a low likelihood but catastrophic severity (like a structural collapse) still needs robust controls.",
  },
  "after-lower": {
    id: "after-lower",
    text: "The rating after controls is lower than before. Record both ratings, so the RAMS shows the controls make a difference.",
  },
  "after-same": {
    id: "after-same",
    text: "The rating after controls is the same as before. Check whether further controls could reduce the likelihood or severity.",
  },
  "after-higher": {
    id: "after-higher",
    text: "The rating after controls is higher than before. Check the scores: controls should not make the risk worse.",
  },
  "reasonably-practicable": {
    id: "reasonably-practicable",
    text: "Whatever the score, HSE says you need to do everything ‘reasonably practicable’ to protect people from harm.",
    cite: { label: "HSE, steps needed to manage risk", url: HSE_RISK_STEPS_URL },
  },
  "scales-vary": {
    id: "scales-vary",
    text: "Scales and bands vary between firms. Use the one your firm or your client uses, and say which in your RAMS. HSE's guidance asks you to decide how likely it is that someone could be harmed and how seriously; it does not set a scoring matrix.",
    cite: { label: "HSE, managing risks", url: HSE_RISK_OVERVIEW_URL },
  },
} as const satisfies Record<string, Note>;

export type NoteId = keyof typeof NOTES;

export interface MatrixResult {
  before: RatedRisk;
  after: RatedRisk | null;
  notes: readonly Note[];
}

function rate(rating: Rating): RatedRisk {
  const value = score(rating.likelihood, rating.severity);
  return { ...rating, score: value, band: bandFor(value) };
}

export function rateRisk(before: Rating, after: Rating | null): MatrixResult {
  const ratedBefore = rate(before);
  const ratedAfter = after ? rate(after) : null;
  const notes: NoteId[] = [];
  const severeNotHigh = (r: RatedRisk) => r.severity === 5 && r.band.id !== "high";
  if (severeNotHigh(ratedBefore) || (ratedAfter !== null && severeNotHigh(ratedAfter))) notes.push("severe-not-high");
  if (ratedAfter !== null) {
    if (ratedAfter.score < ratedBefore.score) notes.push("after-lower");
    else if (ratedAfter.score === ratedBefore.score) notes.push("after-same");
    else notes.push("after-higher");
  }
  notes.push("reasonably-practicable", "scales-vary");
  return { before: ratedBefore, after: ratedAfter, notes: notes.map((id) => NOTES[id]) };
}

/** The 25 cells, severity 5 at the top, likelihood 1 on the left. */
export function matrixRows(): { severity: Level; cells: { likelihood: Level; score: number; band: BandId }[] }[] {
  return [...LEVELS].reverse().map((severity) => ({
    severity,
    cells: LEVELS.map((likelihood) => {
      const value = score(likelihood, severity);
      return { likelihood, score: value, band: bandFor(value).id };
    }),
  }));
}

export const NOT_LEGAL_ADVICE = "This is general guidance, not legal advice.";
